import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Card,
  CardHeader,
  CardBody,
  Icon,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Textarea,
  Switch,
  Button,
  IconButton,
  Alert,
  AlertIcon,
  AlertDescription,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Badge,
  Code,
  Tooltip,
  useColorModeValue,
  VisuallyHidden,
} from '@chakra-ui/react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  FiClock,
  FiCopy,
  FiFilePlus,
  FiLifeBuoy,
  FiPlay,
  FiPlus,
  FiTrash2,
  FiX,
} from 'react-icons/fi'
import { SiSolana } from 'react-icons/si'
import { useMutation } from '@tanstack/react-query'
import { DataTable } from '@/components/common/DataTable'
import { DropdownSelect } from '@/components/common/DropdownSelect'
import type {
  ColumnConfig,
  FilterConfig,
  PaginationConfig,
} from '@/components/common/DataTable/types'
import { useRescueTasks } from '@/hooks/useAdminData'
import { rescueService } from '@/services/admin/rescueService'
import type { RescueRunResult, RescueTask, RescueTaskStatus } from '@/types'
import { formatDateTime, truncateHash } from '@/utils/formatUtils'
import { PageSkeleton } from '@/components/common/LoadingSkeleton'
import { useToast } from '@/hooks/useToast'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { withNetworkStorageKey } from '@/utils/storageKeys'
import { useAdminTabQuery } from '@/navigation/adminTabQuery'

const parseList = (value: string) =>
  value
    .split(/[\n,]/)
    .map(item => item.trim())
    .filter(item => item.length > 0)

const formatMaybeTime = (value: string | null | undefined, fallback: string) =>
  value ? formatDateTime(value, { fallback }) : fallback

type PaymentLogRescueCheckKey = 'payment_log_missing' | 'payment_log_incomplete'
type PaymentLogRescueCheckSelection = PaymentLogRescueCheckKey | 'all'

const ALL_PAYMENT_LOG_RESCUE_CHECK_KEYS: PaymentLogRescueCheckKey[] = [
  'payment_log_missing',
  'payment_log_incomplete',
]

const RESCUE_TAB_KEYS = [
  'payment_log_missing',
  'solana_address_recovery',
  'expired_paid_order_repair',
] as const

export function RescuePage() {
  const { t } = useTranslation()
  const { currentEnv } = useNetworkEnvironment()
  const toast = useToast()
  const cardBg = useColorModeValue('white', 'gray.700')
  const borderColor = useColorModeValue('gray.200', 'gray.600')

  const tabs = useMemo(
    () => [
      {
        key: 'payment_log_missing',
        label: t('rescue.tabs.paymentLogMissing'),
        icon: FiFilePlus,
      },
      {
        key: 'solana_address_recovery',
        label: t('rescue.tabs.solanaAddressRecovery'),
        icon: SiSolana,
      },
      {
        key: 'expired_paid_order_repair',
        label: t('rescue.tabs.expiredPaidOrderRepair'),
        icon: FiClock,
      },
    ],
    [t]
  )

  const TAB_STORAGE_KEY = useMemo(
    () => withNetworkStorageKey('admin_portal_rescue_tab', currentEnv),
    [currentEnv]
  )
  const {
    activeTab: activeTabKey,
    activeTabIndex,
    handleTabChange,
  } = useAdminTabQuery({
    tabs: RESCUE_TAB_KEYS,
    defaultTab: 'payment_log_missing',
    storageKey: TAB_STORAGE_KEY,
  })
  const safeTabIndex = activeTabIndex
  const activeTab = tabs.find(tab => tab.key === activeTabKey) ?? tabs[0]

  const [filters, setFilters] = useState({
    status: '',
    triggerSource: '',
    search: '',
  })
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)

  const [merchantId, setMerchantId] = useState('')
  const [orderIds, setOrderIds] = useState('')
  const [chainIds, setChainIds] = useState('')
  const [limit, setLimit] = useState('50')
  const [dryRun, setDryRun] = useState(true)
  const [lastRuns, setLastRuns] = useState<
    Record<string, RescueRunResult | null>
  >({})
  const [solanaRecoveryOrderId, setSolanaRecoveryOrderId] = useState('')
  const [solanaRecoveryNote, setSolanaRecoveryNote] = useState('')
  const [solanaRecoveryDryRun, setSolanaRecoveryDryRun] = useState(true)
  const [solanaRecoveryContext, setSolanaRecoveryContext] = useState<any>(null)
  const [solanaRecoveryRunResult, setSolanaRecoveryRunResult] =
    useState<any>(null)
  const [expiredRepairOrderId, setExpiredRepairOrderId] = useState('')
  const [expiredRepairTransactionHashes, setExpiredRepairTransactionHashes] =
    useState([''])
  const [expiredRepairNote, setExpiredRepairNote] = useState('')
  const [expiredRepairDryRun, setExpiredRepairDryRun] = useState(true)
  const [expiredRepairRunResult, setExpiredRepairRunResult] =
    useState<any>(null)
  const [checkKeySelection, setCheckKeySelection] =
    useState<PaymentLogRescueCheckSelection>('all')

  const { data, isLoading, error, refetch, isFetching } = useRescueTasks({
    page,
    limit: pageSize,
    status: filters.status || undefined,
    triggerSource: filters.triggerSource || undefined,
    search: filters.search || undefined,
  })

  const triggerMutation = useMutation({
    mutationFn: async () => {
      const payload: {
        checkKeys?: string[]
        chainIds?: string[]
        merchantId?: string
        orderIds?: string[]
        limit?: number
        dryRun?: boolean
      } = {
        dryRun,
      }

      const parsedOrders = parseList(orderIds)
      const parsedChains = parseList(chainIds)
      const parsedLimit = Number(limit)

      if (merchantId.trim()) {
        payload.merchantId = merchantId.trim()
      }
      if (parsedOrders.length > 0) {
        payload.orderIds = parsedOrders
      }
      if (parsedChains.length > 0) {
        payload.chainIds = parsedChains
      }
      if (Number.isFinite(parsedLimit) && parsedLimit > 0) {
        payload.limit = parsedLimit
      }
      payload.checkKeys =
        checkKeySelection === 'all'
          ? ALL_PAYMENT_LOG_RESCUE_CHECK_KEYS
          : [checkKeySelection]

      return rescueService.runRescue(payload, currentEnv)
    },
    onSuccess: response => {
      const runData = response?.data ?? null
      const lastRunKey = `${activeTab.key}:${checkKeySelection}`
      setLastRuns(prev => ({ ...prev, [lastRunKey]: runData }))
      toast.success({
        title: t('rescue.trigger.successTitle'),
        description: t('rescue.trigger.successDescription', {
          queued: runData?.totals?.queued ?? 0,
        }),
        id: 'rescue-trigger-success',
      })
      refetch()
    },
    onError: (err: any) => {
      toast.error({
        title: t('rescue.trigger.errorTitle'),
        description: err?.message || t('rescue.trigger.errorDescription'),
        id: 'rescue-trigger-error',
      })
    },
  })

  const solanaInspectMutation = useMutation({
    mutationFn: async () => {
      const identifier = solanaRecoveryOrderId.trim()
      if (!identifier) {
        throw new Error('Order ID / Business ID is required')
      }
      return rescueService.inspectSolanaAddressRecoveryOrder(
        identifier,
        currentEnv
      )
    },
    onSuccess: response => {
      setSolanaRecoveryContext(response.data ?? null)
      setSolanaRecoveryRunResult(null)
    },
    onError: (err: any) => {
      toast.error({
        title: 'Load recovery context failed',
        description: err?.message,
        id: 'solana-recovery-inspect-error',
      })
    },
  })

  const solanaRecoveryRunMutation = useMutation({
    mutationFn: async () => {
      const identifier = solanaRecoveryOrderId.trim()
      if (!identifier) {
        throw new Error('Order ID / Business ID is required')
      }
      return rescueService.runSolanaAddressRecovery(
        {
          orderIdOrBusinessId: identifier,
          dryRun: solanaRecoveryDryRun,
          note: solanaRecoveryNote.trim() || undefined,
        },
        currentEnv
      )
    },
    onSuccess: response => {
      setSolanaRecoveryRunResult(response.data ?? null)
      toast.success({
        title: solanaRecoveryDryRun
          ? 'Dry-run recovery completed'
          : 'Recovery task created',
        id: 'solana-recovery-dry-run-success',
      })
    },
    onError: (err: any) => {
      toast.error({
        title: solanaRecoveryDryRun
          ? 'Dry-run recovery failed'
          : 'Recovery trigger failed',
        description: err?.message,
        id: 'solana-recovery-dry-run-error',
      })
    },
  })

  const isSolanaRecoveryPending =
    solanaInspectMutation.isPending || solanaRecoveryRunMutation.isPending

  const expiredRepairRunMutation = useMutation({
    mutationFn: async () => {
      const identifier = expiredRepairOrderId.trim()
      const transactionHashes = expiredRepairTransactionHashes
        .map(hash => hash.trim())
        .filter(hash => hash.length > 0)
      if (!identifier) {
        throw new Error('Order ID / Business ID is required')
      }
      if (transactionHashes.length === 0) {
        throw new Error('At least one transaction hash is required')
      }
      if (transactionHashes.length > 20) {
        throw new Error('At most 20 transaction hashes are supported')
      }
      return rescueService.runExpiredPaidOrderRepair(
        {
          orderIdOrBusinessId: identifier,
          transactionHash: transactionHashes[0],
          transactionHashes,
          dryRun: expiredRepairDryRun,
          note: expiredRepairNote.trim() || undefined,
        },
        currentEnv
      )
    },
    onSuccess: response => {
      const result = response.data ?? null
      setExpiredRepairRunResult(result)
      const outcome = result?.outcome ?? 'blocked'
      toast.success({
        title: expiredRepairDryRun
          ? t('rescue.expiredPaidOrderRepair.toast.dryRunTitle')
          : t('rescue.expiredPaidOrderRepair.toast.liveTitle'),
        description: t(`rescue.expiredPaidOrderRepair.outcome.${outcome}`),
        id: 'expired-paid-order-repair-run-success',
      })
    },
    onError: (err: any) => {
      toast.error({
        title: expiredRepairDryRun
          ? t('rescue.expiredPaidOrderRepair.toast.dryRunErrorTitle')
          : t('rescue.expiredPaidOrderRepair.toast.liveErrorTitle'),
        description: err?.message,
        id: 'expired-paid-order-repair-run-error',
      })
    },
  })

  const tasks = data?.data ?? []
  const paginationConfig: PaginationConfig = {
    page,
    pageSize: pageSize,
    total: data?.pagination?.total ?? 0,
    pageSizeOptions: [20, 50, 100],
  }

  const statusColor = (status: RescueTaskStatus) => {
    switch (status) {
      case 'completed':
        return 'green'
      case 'running':
        return 'blue'
      case 'queued':
        return 'yellow'
      case 'failed':
        return 'red'
      case 'skipped':
      default:
        return 'gray'
    }
  }

  const renderOutcome = (task: RescueTask) => {
    if (task.errorMessage) {
      return (
        <Text fontSize="xs" color="red.500" noOfLines={2}>
          {task.errorMessage}
        </Text>
      )
    }

    if (task.result && typeof task.result === 'object') {
      const result = task.result as Record<string, unknown>
      if (
        typeof result.inserted === 'number' ||
        typeof result.skipped === 'number'
      ) {
        return (
          <Text fontSize="xs" color="gray.600">
            {t('rescue.table.resultSummary', {
              inserted: result.inserted ?? 0,
              skipped: result.skipped ?? 0,
            })}
          </Text>
        )
      }
    }

    return (
      <Text fontSize="xs" color="gray.500">
        {t('rescue.table.resultEmpty')}
      </Text>
    )
  }

  const columns: ColumnConfig<RescueTask>[] = [
    {
      key: 'status',
      title: t('rescue.table.status'),
      width: '12%',
      render: (value, row) => (
        <VStack align="start" spacing={1}>
          <Badge colorScheme={statusColor(value)}>
            {t(`rescue.status.${value}`)}
          </Badge>
          <Text fontSize="xs" color="gray.500">
            {t('rescue.table.attempts', {
              attempts: row.attempts,
              max: row.maxAttempts,
            })}
          </Text>
        </VStack>
      ),
    },
    {
      key: 'checkKey',
      title: t('rescue.table.check'),
      width: '18%',
      render: (value, row) => (
        <VStack align="start" spacing={1}>
          <Text fontSize="sm" fontWeight="medium">
            {value}
          </Text>
          <Code fontSize="xs" colorScheme="gray">
            {truncateHash(row.runId)}
          </Code>
        </VStack>
      ),
    },
    {
      key: 'targetId',
      title: t('rescue.table.target'),
      width: '22%',
      render: (value, row) => (
        <VStack align="start" spacing={1}>
          <Text fontSize="sm" fontFamily="mono" noOfLines={1}>
            {value}
          </Text>
          <Text fontSize="xs" color="gray.500">
            {t('rescue.table.chainLabel', {
              chainId: row.chainId ?? t('common.na'),
            })}
          </Text>
        </VStack>
      ),
    },
    {
      key: 'queuedAt',
      title: t('rescue.table.timing'),
      width: '20%',
      render: (_value, row) => {
        const fallback = t('common.na')
        const queuedAt = formatMaybeTime(row.queuedAt, fallback)
        const finishedAt = formatMaybeTime(row.finishedAt, fallback)
        return (
          <VStack align="start" spacing={1}>
            <Text fontSize="xs">{queuedAt}</Text>
            <Text fontSize="xs" color="gray.500">
              {t('rescue.table.finishedAt', { time: finishedAt })}
            </Text>
          </VStack>
        )
      },
    },
    {
      key: 'result',
      title: t('rescue.table.outcome'),
      width: '28%',
      render: (_value, row) => renderOutcome(row),
    },
  ]

  const filtersConfig: FilterConfig[] = [
    {
      key: 'search',
      type: 'text',
      label: t('rescue.filters.search'),
      placeholder: t('rescue.filters.searchPlaceholder'),
      width: '260px',
    },
    {
      key: 'status',
      type: 'select',
      label: t('rescue.filters.status'),
      placeholder: t('rescue.filters.status'),
      options: [
        { value: 'queued', label: t('rescue.status.queued') },
        { value: 'running', label: t('rescue.status.running') },
        { value: 'completed', label: t('rescue.status.completed') },
        { value: 'skipped', label: t('rescue.status.skipped') },
        { value: 'failed', label: t('rescue.status.failed') },
      ],
      width: '180px',
    },
    {
      key: 'triggerSource',
      type: 'select',
      label: t('rescue.filters.triggerSource'),
      placeholder: t('rescue.filters.triggerSource'),
      options: [
        { value: 'manual', label: t('rescue.triggerSource.manual') },
        { value: 'cron', label: t('rescue.triggerSource.cron') },
      ],
      width: '180px',
    },
  ]

  const lastRunKey = activeTab ? `${activeTab.key}:${checkKeySelection}` : ''
  const lastRun = activeTab ? (lastRuns[lastRunKey] ?? null) : null
  const handleCopyLastRun = async () => {
    if (!lastRun) {
      return
    }
    const payload = JSON.stringify(lastRun, null, 2)

    try {
      if (
        typeof window === 'undefined' ||
        !window.isSecureContext ||
        !navigator.clipboard?.writeText
      ) {
        throw new Error('Clipboard API unavailable in this browser or context.')
      }
      await navigator.clipboard.writeText(payload)

      toast.success({
        title: t('common.copySuccess'),
      })
    } catch (error: any) {
      toast.error({
        title: t('common.copyFailed'),
        description: error?.message,
      })
    }
  }

  const handleFiltersChange = (newFilters: Record<string, any>) => {
    setFilters({
      status: newFilters.status || '',
      triggerSource: newFilters.triggerSource || '',
      search: newFilters.search || '',
    })
    setPage(1)
  }

  const renderAddressLine = (label: string, value: unknown) => (
    <VStack align="start" spacing={1}>
      <Text fontSize="xs" color="gray.500">
        {label}
      </Text>
      <Code fontSize="xs" whiteSpace="normal" wordBreak="break-all">
        {typeof value === 'string' && value.length > 0 ? value : t('common.na')}
      </Code>
    </VStack>
  )

  const renderMetaLine = (label: string, value: unknown) => (
    <VStack align="start" spacing={1}>
      <Text fontSize="xs" color="gray.500">
        {label}
      </Text>
      <Text fontSize="sm" wordBreak="break-word">
        {typeof value === 'string' && value.length > 0 ? value : t('common.na')}
      </Text>
    </VStack>
  )

  const updateExpiredRepairTransactionHash = (index: number, value: string) => {
    const pastedHashes = parseList(value)
    setExpiredRepairTransactionHashes(prev => {
      if (pastedHashes.length > 1) {
        const next = [...prev]
        next.splice(index, 1, ...pastedHashes)
        return next.slice(0, 20)
      }
      return prev.map((item, itemIndex) => (itemIndex === index ? value : item))
    })
  }

  const addExpiredRepairTransactionHash = () => {
    setExpiredRepairTransactionHashes(prev =>
      prev.length >= 20 ? prev : [...prev, '']
    )
  }

  const removeExpiredRepairTransactionHash = (index: number) => {
    setExpiredRepairTransactionHashes(prev =>
      prev.length <= 1
        ? ['']
        : prev.filter((_, itemIndex) => itemIndex !== index)
    )
  }

  const renderSolanaRecoveryPanel = () => (
    <VStack align="stretch" spacing={3} mt={2}>
      <Card bg={cardBg} borderColor={borderColor}>
        <CardHeader py={2} px={3}>
          <Heading size="sm">
            {t('rescue.solanaAddressRecovery.contextTitle')}
          </Heading>
        </CardHeader>
        <CardBody pt={0} px={3} pb={3}>
          <VStack align="stretch" spacing={3}>
            <FormControl>
              <FormLabel fontSize="sm">
                {t('rescue.solanaAddressRecovery.fields.orderId')}
              </FormLabel>
              <HStack spacing={2}>
                <Input
                  size="sm"
                  value={solanaRecoveryOrderId}
                  onChange={event =>
                    setSolanaRecoveryOrderId(event.target.value)
                  }
                  placeholder={t(
                    'rescue.solanaAddressRecovery.placeholders.orderId'
                  )}
                  isDisabled={isSolanaRecoveryPending}
                />
                <IconButton
                  aria-label={t('common.clear')}
                  icon={<FiX />}
                  size="sm"
                  variant="ghost"
                  onClick={() => setSolanaRecoveryOrderId('')}
                  isDisabled={isSolanaRecoveryPending || !solanaRecoveryOrderId}
                />
              </HStack>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">
                {t('rescue.solanaAddressRecovery.fields.note')}
              </FormLabel>
              <HStack spacing={2}>
                <Input
                  size="sm"
                  value={solanaRecoveryNote}
                  onChange={event => setSolanaRecoveryNote(event.target.value)}
                  placeholder={t(
                    'rescue.solanaAddressRecovery.placeholders.note'
                  )}
                  isDisabled={isSolanaRecoveryPending}
                />
                <IconButton
                  aria-label={t('common.clear')}
                  icon={<FiX />}
                  size="sm"
                  variant="ghost"
                  onClick={() => setSolanaRecoveryNote('')}
                  isDisabled={isSolanaRecoveryPending || !solanaRecoveryNote}
                />
              </HStack>
            </FormControl>
            <Button
              size="sm"
              onClick={() => solanaInspectMutation.mutate()}
              isLoading={solanaInspectMutation.isPending}
              alignSelf="flex-start"
            >
              {t('rescue.solanaAddressRecovery.actions.loadContext')}
            </Button>
          </VStack>
        </CardBody>
      </Card>

      <Card bg={cardBg} borderColor={borderColor}>
        <CardHeader py={2} px={3}>
          <HStack justify="space-between">
            <Heading size="sm">
              {t('rescue.solanaAddressRecovery.runTitle')}
            </Heading>
            <Badge colorScheme={solanaRecoveryDryRun ? 'orange' : 'blue'}>
              {solanaRecoveryDryRun
                ? t('rescue.trigger.dryRun')
                : t('rescue.trigger.live')}
            </Badge>
          </HStack>
        </CardHeader>
        <CardBody pt={0} px={3} pb={3}>
          <VStack spacing={3} align="stretch">
            <FormControl>
              <HStack justify="space-between">
                <FormLabel fontSize="sm" mb={0}>
                  {t('rescue.trigger.fields.dryRun')}
                </FormLabel>
                <Switch
                  isChecked={solanaRecoveryDryRun}
                  onChange={event =>
                    setSolanaRecoveryDryRun(event.target.checked)
                  }
                  isDisabled={isSolanaRecoveryPending}
                  colorScheme="blue"
                />
              </HStack>
              <FormHelperText fontSize="xs">
                {solanaRecoveryDryRun
                  ? t('rescue.solanaAddressRecovery.helpers.dryRun')
                  : t('rescue.solanaAddressRecovery.helpers.live')}
              </FormHelperText>
            </FormControl>
            <Button
              size="sm"
              colorScheme="blue"
              onClick={() => solanaRecoveryRunMutation.mutate()}
              isLoading={solanaRecoveryRunMutation.isPending}
              alignSelf="flex-start"
            >
              {solanaRecoveryDryRun
                ? t('rescue.solanaAddressRecovery.actions.runDryRun')
                : t('rescue.solanaAddressRecovery.actions.runLive')}
            </Button>
          </VStack>
        </CardBody>
      </Card>

      {solanaRecoveryContext && (
        <Card bg={cardBg} borderColor={borderColor}>
          <CardHeader py={2} px={3}>
            <HStack justify="space-between">
              <Heading size="sm">
                {t('rescue.solanaAddressRecovery.result.contextTitle')}
              </Heading>
              <Badge
                colorScheme={
                  solanaRecoveryContext.recoveryEligible ? 'green' : 'orange'
                }
              >
                {solanaRecoveryContext.recoveryEligible
                  ? t('rescue.solanaAddressRecovery.status.eligible')
                  : t('rescue.solanaAddressRecovery.status.blocked')}
              </Badge>
            </HStack>
          </CardHeader>
          <CardBody pt={0} px={3} pb={3}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
              {renderMetaLine(
                t('rescue.solanaAddressRecovery.fields.currentNetwork'),
                solanaRecoveryContext.network
              )}
              {renderMetaLine(
                t('rescue.solanaAddressRecovery.fields.paymentToken'),
                solanaRecoveryContext.paymentToken
              )}
              {renderAddressLine(
                t('rescue.solanaAddressRecovery.fields.paymentAddress'),
                solanaRecoveryContext.paymentAddress
              )}
              {renderAddressLine(
                t('rescue.solanaAddressRecovery.fields.vaultTokenAccount'),
                solanaRecoveryContext.vaultTokenAccount
              )}
              {renderAddressLine(
                t('rescue.solanaAddressRecovery.fields.nestedTokenAccount'),
                solanaRecoveryContext.nestedTokenAccount
              )}
              {renderAddressLine(
                t('rescue.solanaAddressRecovery.fields.tokenMint'),
                solanaRecoveryContext.tokenMint
              )}
              <Stat>
                <StatLabel>
                  {t('rescue.solanaAddressRecovery.fields.vaultBalance')}
                </StatLabel>
                <StatNumber>
                  {solanaRecoveryContext.vaultBalance ?? '0'}
                </StatNumber>
              </Stat>
              <Stat>
                <StatLabel>
                  {t('rescue.solanaAddressRecovery.fields.nestedBalance')}
                </StatLabel>
                <StatNumber>
                  {solanaRecoveryContext.nestedBalance ?? '0'}
                </StatNumber>
              </Stat>
              <VStack align="start" spacing={1}>
                <Text fontSize="xs" color="gray.500">
                  {t('rescue.solanaAddressRecovery.fields.orderStatus')}
                </Text>
                <Badge>
                  {solanaRecoveryContext.orderStatus ?? t('common.na')}
                </Badge>
              </VStack>
              <VStack align="start" spacing={1}>
                <Text fontSize="xs" color="gray.500">
                  {t('rescue.solanaAddressRecovery.fields.latestTaskStatus')}
                </Text>
                <Badge colorScheme="gray">
                  {solanaRecoveryContext.latestTaskStatus ?? t('common.na')}
                </Badge>
              </VStack>
              <VStack align="start" spacing={1}>
                <Text fontSize="xs" color="gray.500">
                  {t('rescue.solanaAddressRecovery.fields.blockedReason')}
                </Text>
                <Text fontSize="sm">
                  {solanaRecoveryContext.recoveryBlockedReason ??
                    t('common.na')}
                </Text>
              </VStack>
              {renderAddressLine(
                t('rescue.solanaAddressRecovery.fields.latestTx'),
                solanaRecoveryContext.latestTxHash
              )}
            </SimpleGrid>
          </CardBody>
        </Card>
      )}

      {solanaRecoveryRunResult && (
        <Card bg={cardBg} borderColor={borderColor}>
          <CardHeader py={2} px={3}>
            <Heading size="sm">
              {solanaRecoveryRunResult.dryRun
                ? t('rescue.solanaAddressRecovery.result.dryRunTitle')
                : t('rescue.solanaAddressRecovery.result.runTitle')}
            </Heading>
          </CardHeader>
          <CardBody pt={0} px={3} pb={3}>
            <Code
              display="block"
              whiteSpace="pre-wrap"
              p={2}
              borderRadius="md"
              fontSize="xs"
            >
              {JSON.stringify(solanaRecoveryRunResult, null, 2)}
            </Code>
          </CardBody>
        </Card>
      )}
    </VStack>
  )

  const renderExpiredPaidOrderRepairPanel = () => {
    const outcome = expiredRepairRunResult?.outcome
    const isLive = !expiredRepairDryRun
    return (
      <VStack align="stretch" spacing={3} mt={2}>
        <Card bg={cardBg} borderColor={borderColor}>
          <CardHeader py={2} px={3}>
            <HStack justify="space-between">
              <Heading size="sm">
                {t('rescue.expiredPaidOrderRepair.runTitle')}
              </Heading>
              <Badge colorScheme={expiredRepairDryRun ? 'orange' : 'red'}>
                {expiredRepairDryRun
                  ? t('rescue.trigger.dryRun')
                  : t('rescue.trigger.live')}
              </Badge>
            </HStack>
          </CardHeader>
          <CardBody pt={0} px={3} pb={3}>
            <VStack spacing={3} align="stretch">
              <FormControl isRequired>
                <FormLabel fontSize="sm">
                  {t('rescue.expiredPaidOrderRepair.fields.orderId')}
                </FormLabel>
                <HStack spacing={2}>
                  <Input
                    size="sm"
                    value={expiredRepairOrderId}
                    onChange={event =>
                      setExpiredRepairOrderId(event.target.value)
                    }
                    placeholder={t(
                      'rescue.expiredPaidOrderRepair.placeholders.orderId'
                    )}
                    isDisabled={expiredRepairRunMutation.isPending}
                  />
                  <IconButton
                    aria-label={t('common.clear')}
                    icon={<FiX />}
                    size="sm"
                    variant="ghost"
                    onClick={() => setExpiredRepairOrderId('')}
                    isDisabled={
                      expiredRepairRunMutation.isPending ||
                      !expiredRepairOrderId
                    }
                  />
                </HStack>
                <FormHelperText fontSize="xs">
                  {t('rescue.expiredPaidOrderRepair.helpers.orderId')}
                </FormHelperText>
              </FormControl>
              <FormControl isRequired>
                <HStack justify="space-between" mb={2}>
                  <FormLabel fontSize="sm" mb={0}>
                    {t('rescue.expiredPaidOrderRepair.fields.transactionHash')}
                  </FormLabel>
                  <Button
                    size="xs"
                    variant="outline"
                    leftIcon={<FiPlus />}
                    onClick={addExpiredRepairTransactionHash}
                    isDisabled={
                      expiredRepairRunMutation.isPending ||
                      expiredRepairTransactionHashes.length >= 20
                    }
                  >
                    {t(
                      'rescue.expiredPaidOrderRepair.actions.addTransactionHash'
                    )}
                  </Button>
                </HStack>
                <VStack align="stretch" spacing={2}>
                  {expiredRepairTransactionHashes.map((hash, index) => (
                    <HStack key={index} align="start" spacing={2}>
                      <Input
                        size="sm"
                        value={hash}
                        onChange={event =>
                          updateExpiredRepairTransactionHash(
                            index,
                            event.target.value
                          )
                        }
                        placeholder={t(
                          'rescue.expiredPaidOrderRepair.placeholders.transactionHash',
                          { index: index + 1 }
                        )}
                        isDisabled={expiredRepairRunMutation.isPending}
                        fontFamily="mono"
                      />
                      <IconButton
                        aria-label={t('common.clear')}
                        icon={<FiX />}
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          updateExpiredRepairTransactionHash(index, '')
                        }
                        isDisabled={expiredRepairRunMutation.isPending || !hash}
                      />
                      <IconButton
                        aria-label={t(
                          'rescue.expiredPaidOrderRepair.actions.removeTransactionHash'
                        )}
                        icon={<FiTrash2 />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() =>
                          removeExpiredRepairTransactionHash(index)
                        }
                        isDisabled={
                          expiredRepairRunMutation.isPending ||
                          expiredRepairTransactionHashes.length <= 1
                        }
                      />
                    </HStack>
                  ))}
                </VStack>
                <FormHelperText fontSize="xs">
                  {t('rescue.expiredPaidOrderRepair.helpers.transactionHash', {
                    count: expiredRepairTransactionHashes.length,
                  })}
                </FormHelperText>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">
                  {t('rescue.expiredPaidOrderRepair.fields.note')}
                </FormLabel>
                <HStack spacing={2}>
                  <Input
                    size="sm"
                    value={expiredRepairNote}
                    onChange={event => setExpiredRepairNote(event.target.value)}
                    placeholder={t(
                      'rescue.expiredPaidOrderRepair.placeholders.note'
                    )}
                    isDisabled={expiredRepairRunMutation.isPending}
                  />
                  <IconButton
                    aria-label={t('common.clear')}
                    icon={<FiX />}
                    size="sm"
                    variant="ghost"
                    onClick={() => setExpiredRepairNote('')}
                    isDisabled={
                      expiredRepairRunMutation.isPending || !expiredRepairNote
                    }
                  />
                </HStack>
              </FormControl>
              <FormControl>
                <HStack justify="space-between">
                  <FormLabel fontSize="sm" mb={0}>
                    {t('rescue.trigger.fields.dryRun')}
                  </FormLabel>
                  <Switch
                    isChecked={expiredRepairDryRun}
                    onChange={event =>
                      setExpiredRepairDryRun(event.target.checked)
                    }
                    isDisabled={expiredRepairRunMutation.isPending}
                    colorScheme="blue"
                  />
                </HStack>
                <FormHelperText fontSize="xs">
                  {expiredRepairDryRun
                    ? t('rescue.expiredPaidOrderRepair.helpers.dryRun')
                    : t('rescue.expiredPaidOrderRepair.helpers.live')}
                </FormHelperText>
              </FormControl>
              {isLive && (
                <Alert
                  status="error"
                  variant="left-accent"
                  borderRadius="md"
                  py={2}
                >
                  <AlertIcon />
                  <AlertDescription fontSize="xs">
                    {t('rescue.expiredPaidOrderRepair.liveWarning')}
                  </AlertDescription>
                </Alert>
              )}
              <Button
                size="sm"
                colorScheme={expiredRepairDryRun ? 'blue' : 'red'}
                onClick={() => expiredRepairRunMutation.mutate()}
                isLoading={expiredRepairRunMutation.isPending}
                leftIcon={<FiPlay />}
                alignSelf="flex-start"
              >
                {expiredRepairDryRun
                  ? t('rescue.expiredPaidOrderRepair.actions.runDryRun')
                  : t('rescue.expiredPaidOrderRepair.actions.runLive')}
              </Button>
            </VStack>
          </CardBody>
        </Card>

        {expiredRepairRunResult && (
          <Card bg={cardBg} borderColor={borderColor}>
            <CardHeader py={2} px={3}>
              <HStack justify="space-between">
                <Heading size="sm">
                  {t('rescue.expiredPaidOrderRepair.result.title')}
                </Heading>
                <Badge
                  colorScheme={
                    outcome === 'applied' || outcome === 'eligible'
                      ? 'green'
                      : outcome === 'applied_with_warnings'
                        ? 'orange'
                        : 'red'
                  }
                >
                  {outcome
                    ? t(`rescue.expiredPaidOrderRepair.outcome.${outcome}`)
                    : ''}
                </Badge>
              </HStack>
            </CardHeader>
            <CardBody pt={0} px={3} pb={3}>
              <VStack align="stretch" spacing={3}>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                  {renderMetaLine(
                    t('rescue.expiredPaidOrderRepair.result.currentStatus'),
                    expiredRepairRunResult.currentStatus
                  )}
                  {renderMetaLine(
                    t('rescue.expiredPaidOrderRepair.result.targetStatus'),
                    expiredRepairRunResult.targetStatus
                  )}
                  {renderMetaLine(
                    t('rescue.expiredPaidOrderRepair.result.blockedReason'),
                    expiredRepairRunResult.blockedReason
                  )}
                  {renderMetaLine(
                    t('rescue.expiredPaidOrderRepair.result.expectedAmount'),
                    expiredRepairRunResult.expectedAmountInSmallestUnit
                  )}
                  {renderMetaLine(
                    t('rescue.expiredPaidOrderRepair.result.matchedAmount'),
                    expiredRepairRunResult.matchedAmountInSmallestUnit
                  )}
                  {renderMetaLine(
                    t('rescue.expiredPaidOrderRepair.result.finalPaidAmount'),
                    expiredRepairRunResult.finalPaidAmountInSmallestUnit
                  )}
                </SimpleGrid>
                {renderAddressLine(
                  t('rescue.expiredPaidOrderRepair.result.paymentAddress'),
                  expiredRepairRunResult.publicPaymentAddress
                )}
                {renderAddressLine(
                  expiredRepairRunResult.operationalAddressType ===
                    'vault_token_account'
                    ? t(
                        'rescue.expiredPaidOrderRepair.result.vaultTokenAccount'
                      )
                    : t('rescue.expiredPaidOrderRepair.result.contractAddress'),
                  expiredRepairRunResult.operationalAddress
                )}
                {renderAddressLine(
                  t('rescue.expiredPaidOrderRepair.result.tokenAddress'),
                  expiredRepairRunResult.tokenAddress
                )}
                {Array.isArray(expiredRepairRunResult.transactionResults) &&
                  expiredRepairRunResult.transactionResults.length > 0 && (
                    <VStack align="stretch" spacing={2}>
                      <Text fontSize="sm" fontWeight="medium">
                        {t(
                          'rescue.expiredPaidOrderRepair.result.transactionResults'
                        )}
                      </Text>
                      <VStack align="stretch" spacing={2}>
                        {expiredRepairRunResult.transactionResults.map(
                          (tx: any) => (
                            <Box
                              key={tx.transactionHash}
                              borderWidth="1px"
                              borderColor={borderColor}
                              borderRadius="md"
                              p={2}
                            >
                              <VStack align="stretch" spacing={2}>
                                <HStack justify="space-between" align="start">
                                  <Code
                                    fontSize="xs"
                                    whiteSpace="normal"
                                    wordBreak="break-all"
                                  >
                                    {tx.transactionHash}
                                  </Code>
                                  <Badge
                                    colorScheme={
                                      tx.status === 'matched'
                                        ? 'green'
                                        : tx.status === 'not_evaluated'
                                          ? 'gray'
                                          : 'orange'
                                    }
                                  >
                                    {t(
                                      `rescue.expiredPaidOrderRepair.transactionStatus.${tx.status}`
                                    )}
                                  </Badge>
                                </HStack>
                                <SimpleGrid
                                  columns={{ base: 1, md: 3 }}
                                  spacing={2}
                                >
                                  {renderMetaLine(
                                    t(
                                      'rescue.expiredPaidOrderRepair.result.txMatchedAmount'
                                    ),
                                    tx.matchedAmountInSmallestUnit
                                  )}
                                  {renderMetaLine(
                                    t(
                                      'rescue.expiredPaidOrderRepair.result.txMatchedTransfers'
                                    ),
                                    String(tx.matchedTransferCount ?? 0)
                                  )}
                                  {renderMetaLine(
                                    t(
                                      'rescue.expiredPaidOrderRepair.result.txAlreadyLogged'
                                    ),
                                    String(tx.alreadyLoggedCount ?? 0)
                                  )}
                                </SimpleGrid>
                                {Array.isArray(tx.paymentLogIds) &&
                                  tx.paymentLogIds.length > 0 && (
                                    <VStack align="start" spacing={1}>
                                      <Text fontSize="xs" color="gray.500">
                                        {t(
                                          'rescue.expiredPaidOrderRepair.result.paymentLogIds'
                                        )}
                                      </Text>
                                      <HStack spacing={1} flexWrap="wrap">
                                        {tx.paymentLogIds.map(
                                          (paymentLogId: string) => (
                                            <Code
                                              key={paymentLogId}
                                              fontSize="xs"
                                            >
                                              {paymentLogId}
                                            </Code>
                                          )
                                        )}
                                      </HStack>
                                    </VStack>
                                  )}
                                {tx.rejectedReason && (
                                  <Text fontSize="xs" color="orange.600">
                                    {t(
                                      'rescue.expiredPaidOrderRepair.result.rejectedReason'
                                    )}
                                    : {tx.rejectedReason}
                                  </Text>
                                )}
                              </VStack>
                            </Box>
                          )
                        )}
                      </VStack>
                    </VStack>
                  )}
                <Code
                  display="block"
                  whiteSpace="pre-wrap"
                  p={2}
                  borderRadius="md"
                  fontSize="xs"
                >
                  {JSON.stringify(expiredRepairRunResult, null, 2)}
                </Code>
              </VStack>
            </CardBody>
          </Card>
        )}
      </VStack>
    )
  }

  if (isLoading && tasks.length === 0) {
    return (
      <Box role="status" aria-busy="true">
        <PageSkeleton minHeight="320px" cardCount={2} />
        <VisuallyHidden>{t('common.loading')}</VisuallyHidden>
      </Box>
    )
  }

  if (error) {
    return (
      <Alert status="error">
        <AlertIcon />
        {t('rescue.loadError')}
      </Alert>
    )
  }

  return (
    <Box>
      <VStack align="stretch" spacing={3}>
        <HStack justify="space-between" align="start">
          <Box>
            <HStack spacing={2} mb={1}>
              <Icon as={FiLifeBuoy} boxSize={6} color="blue.500" />
              <Heading size="lg">{t('rescue.title')}</Heading>
            </HStack>
            <Text color="gray.600" fontSize="sm">
              {t('rescue.subtitle')}
            </Text>
          </Box>
        </HStack>

        <Tabs
          colorScheme="blue"
          variant="enclosed"
          index={safeTabIndex}
          onChange={index => {
            handleTabChange(index)
            setPage(1)
          }}
        >
          <TabList
            overflowX={{ base: 'auto', md: 'visible' }}
            overflowY="hidden"
          >
            {tabs.map(tab => (
              <Tab
                key={tab.key}
                aria-label={tab.label}
                minW="56px"
                justifyContent="center"
              >
                <Tooltip label={tab.label} hasArrow>
                  <Box display="flex" alignItems="center">
                    <Icon as={tab.icon} boxSize={6} />
                  </Box>
                </Tooltip>
              </Tab>
            ))}
          </TabList>
          <TabPanels>
            {tabs.map(tab => (
              <TabPanel key={tab.key} p={0}>
                {tab.key === 'solana_address_recovery' ? (
                  renderSolanaRecoveryPanel()
                ) : tab.key === 'expired_paid_order_repair' ? (
                  renderExpiredPaidOrderRepairPanel()
                ) : (
                  <VStack align="stretch" spacing={3} mt={2}>
                    <Card bg={cardBg} borderColor={borderColor}>
                      <CardHeader py={2} px={3}>
                        <HStack justify="space-between">
                          <Heading size="sm">
                            {t('rescue.trigger.title')}
                          </Heading>
                          <Badge colorScheme={dryRun ? 'orange' : 'blue'}>
                            {dryRun
                              ? t('rescue.trigger.dryRun')
                              : t('rescue.trigger.live')}
                          </Badge>
                        </HStack>
                      </CardHeader>
                      <CardBody pt={0} px={3} pb={3}>
                        <VStack spacing={3} align="stretch">
                          <FormControl>
                            <FormLabel fontSize="sm">
                              {t('rescue.trigger.fields.checkKeys')}
                            </FormLabel>
                            <DropdownSelect
                              size="sm"
                              value={checkKeySelection}
                              options={[
                                {
                                  value: 'payment_log_missing',
                                  label: t(
                                    'rescue.trigger.checkOptions.missing'
                                  ),
                                },
                                {
                                  value: 'payment_log_incomplete',
                                  label: t(
                                    'rescue.trigger.checkOptions.incomplete'
                                  ),
                                },
                                {
                                  value: 'all',
                                  label: t('rescue.trigger.checkOptions.all'),
                                },
                              ]}
                              onChange={value => {
                                setCheckKeySelection(
                                  value as PaymentLogRescueCheckSelection
                                )
                                setPage(1)
                              }}
                              ariaLabel={t('rescue.trigger.fields.checkKeys')}
                              isDisabled={triggerMutation.isPending}
                            />
                            <FormHelperText fontSize="xs">
                              {t('rescue.trigger.helpers.checkKeys')}
                            </FormHelperText>
                          </FormControl>
                          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                            <FormControl>
                              <FormLabel fontSize="sm">
                                {t('rescue.trigger.fields.merchantId')}
                              </FormLabel>
                              <Input
                                value={merchantId}
                                onChange={event =>
                                  setMerchantId(event.target.value)
                                }
                                size="sm"
                                placeholder="mch_..."
                                isDisabled={triggerMutation.isPending}
                              />
                              <FormHelperText fontSize="xs">
                                {t('rescue.trigger.helpers.merchantId')}
                              </FormHelperText>
                            </FormControl>
                            <FormControl>
                              <FormLabel fontSize="sm">
                                {t('rescue.trigger.fields.chainIds')}
                              </FormLabel>
                              <Input
                                value={chainIds}
                                onChange={event =>
                                  setChainIds(event.target.value)
                                }
                                size="sm"
                                placeholder="97, 84532, 11155111"
                                isDisabled={triggerMutation.isPending}
                              />
                              <FormHelperText fontSize="xs">
                                {t('rescue.trigger.helpers.chainIds')}
                              </FormHelperText>
                            </FormControl>
                          </SimpleGrid>
                          <FormControl>
                            <FormLabel fontSize="sm">
                              {t('rescue.trigger.fields.orderIds')}
                            </FormLabel>
                            <Textarea
                              value={orderIds}
                              onChange={event =>
                                setOrderIds(event.target.value)
                              }
                              size="sm"
                              rows={3}
                              placeholder="ord_..."
                              isDisabled={triggerMutation.isPending}
                            />
                            <FormHelperText fontSize="xs">
                              {t('rescue.trigger.helpers.orderIds')}
                            </FormHelperText>
                          </FormControl>
                          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                            <FormControl>
                              <FormLabel fontSize="sm">
                                {t('rescue.trigger.fields.limit')}
                              </FormLabel>
                              <Input
                                type="number"
                                value={limit}
                                onChange={event => setLimit(event.target.value)}
                                size="sm"
                                min={1}
                                isDisabled={triggerMutation.isPending}
                              />
                              <FormHelperText fontSize="xs">
                                {t('rescue.trigger.helpers.limit')}
                              </FormHelperText>
                            </FormControl>
                            <FormControl>
                              <HStack justify="space-between">
                                <FormLabel fontSize="sm" mb={0}>
                                  {t('rescue.trigger.fields.dryRun')}
                                </FormLabel>
                                <Switch
                                  isChecked={dryRun}
                                  onChange={event =>
                                    setDryRun(event.target.checked)
                                  }
                                  isDisabled={triggerMutation.isPending}
                                  colorScheme="blue"
                                />
                              </HStack>
                              <FormHelperText fontSize="xs">
                                {t('rescue.trigger.helpers.dryRun')}
                              </FormHelperText>
                            </FormControl>
                          </SimpleGrid>
                          <Button
                            colorScheme="blue"
                            onClick={() => triggerMutation.mutate()}
                            isLoading={triggerMutation.isPending}
                            loadingText={t('rescue.trigger.loading')}
                            leftIcon={<FiPlay />}
                            size="sm"
                            alignSelf="flex-start"
                          >
                            {t('rescue.trigger.button')}
                          </Button>
                        </VStack>
                      </CardBody>
                    </Card>

                    <Card bg={cardBg} borderColor={borderColor}>
                      <CardHeader py={2} px={3}>
                        <HStack justify="space-between" align="center">
                          <Heading size="sm">
                            {t('rescue.results.title')}
                          </Heading>
                          <Tooltip label={t('common.copy')}>
                            <IconButton
                              aria-label={t('common.copy')}
                              icon={<FiCopy />}
                              size="xs"
                              variant="ghost"
                              onClick={handleCopyLastRun}
                              isDisabled={!lastRun}
                            />
                          </Tooltip>
                        </HStack>
                      </CardHeader>
                      <CardBody pt={0} px={3} pb={3}>
                        {lastRun ? (
                          <VStack align="stretch" spacing={3}>
                            <HStack spacing={3} flexWrap="wrap">
                              <Badge colorScheme="blue">
                                {t(
                                  `rescue.triggerSource.${lastRun.triggerSource}`
                                )}
                              </Badge>
                              {lastRun.dryRun && (
                                <Badge colorScheme="orange">
                                  {t('rescue.trigger.dryRun')}
                                </Badge>
                              )}
                              <Code fontSize="xs">{lastRun.runId}</Code>
                            </HStack>
                            <SimpleGrid
                              columns={{ base: 2, md: 4 }}
                              spacing={2}
                            >
                              <Stat>
                                <StatLabel>
                                  {t('rescue.results.candidates')}
                                </StatLabel>
                                <StatNumber>
                                  {lastRun.totals.candidates}
                                </StatNumber>
                              </Stat>
                              <Stat>
                                <StatLabel>
                                  {t('rescue.results.queued')}
                                </StatLabel>
                                <StatNumber>{lastRun.totals.queued}</StatNumber>
                              </Stat>
                              <Stat>
                                <StatLabel>
                                  {t('rescue.results.skipped')}
                                </StatLabel>
                                <StatNumber>
                                  {lastRun.totals.skipped}
                                </StatNumber>
                              </Stat>
                              <Stat>
                                <StatLabel>
                                  {t('rescue.results.failed')}
                                </StatLabel>
                                <StatNumber>{lastRun.totals.failed}</StatNumber>
                              </Stat>
                            </SimpleGrid>
                            <Code
                              display="block"
                              whiteSpace="pre-wrap"
                              p={2}
                              borderRadius="md"
                              fontSize="xs"
                            >
                              {JSON.stringify(lastRun, null, 2)}
                            </Code>
                          </VStack>
                        ) : (
                          <Text color="gray.500" fontSize="sm">
                            {t('rescue.results.empty')}
                          </Text>
                        )}
                      </CardBody>
                    </Card>

                    <DataTable<RescueTask>
                      data={tasks}
                      loading={isLoading}
                      error={error}
                      columns={columns}
                      filters={filtersConfig}
                      onFiltersChange={handleFiltersChange}
                      pagination={paginationConfig}
                      onPageChange={setPage}
                      onPageSizeChange={size => {
                        setPageSize(size)
                        setPage(1)
                      }}
                      size="sm"
                      emptyMessage={t('rescue.table.empty')}
                      showRefresh
                      onRefresh={refetch}
                      refreshing={isFetching}
                    />
                  </VStack>
                )}
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      </VStack>
    </Box>
  )
}
